import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate, getLicenseStatus } from 'shared/helpers';
import { fetchAccountLicenses } from '../../../actions';
import '../../styles.scss';

const PAGE_SIZE = 20;

const LicensesTab = ({ accountId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [retryToken, setRetryToken] = useState(0);

  const handleRowClick = (row) => {
    const licenseId = get(row, 'id');
    if (licenseId) {
      history.push(`/licenses/${licenseId}`);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    let isStale = false;
    setLoading(true);
    setError(false);
    fetchAccountLicenses(accountId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const totalCount = Array.isArray(payload)
          ? results.length
          : get(payload, 'count', results.length);
        if (!isStale) {
          setLicenses(results);
          setCount(totalCount);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isStale) {
          setError(true);
          setLicenses([]);
          setCount(0);
          setLoading(false);
        }
      });
  }, [accountId, currentSort, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('License key'),
      id: 'license_key',
      accessor: (row) =>
        get(row, 'license_key') ?? get(row, 'licenseKey') ?? null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('Product'),
      accessor: 'product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('License Type'),
      accessor: 'license_type',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(t(rowData.value));
      },
      width: 120,
    },
    {
      Header: t('Expiration Date'),
      id: 'expiration_date',
      accessor: (row) =>
        get(row, 'expiration_date') ??
        get(row, 'expires_at') ??
        get(row, 'valid_until') ??
        get(row, 'maintenance_period') ??
        null,
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 160,
    },
    {
      Header: t('Times activated'),
      accessor: 'times_activated',
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(rowData.value, 0);
      },
      width: 140,
    },
    {
      Header: t('Is trial'),
      accessor: 'is_trial',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 90,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        const { status, message } = getLicenseStatus(rowData, t);
        return <StatusCard status={status} text={message} />;
      },
      width: 150,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading licenses')} />;
  }

  if (error) {
    return (
      <div className="CustomerAccount-error">
        <p>{t('Unable to load licenses for this account')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!licenses.length) {
    return (
      <div className="CustomerAccount-empty">
        {t('No licenses for this account')}
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <div className="CustomerAccount-list">
      <List
        data={licenses}
        columns={columns}
        page={page}
        pages={pages}
        loading={loading}
        manual
        minRows={licenses.length || 5}
        showPagination={pages > 1}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
        defaultPageSize={PAGE_SIZE}
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

LicensesTab.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicensesTab;
